import { memo, useCallback, useEffect, useId } from 'react';
import PropTypes from 'prop-types';

/**
 * Track / thumb measures per size. `medium` matches the 44×24 toggles the apps
 * already hand-rolled (agentic-studio `ToggleSwitch`, `ActionToggle`); `small`
 * matches the 36×20 one in dashboards (`SettingToggle`). The thumb sits 2px in
 * from every edge in both, so `translate-x` is `track - thumb - 2px` when on.
 */
const SIZE_CLASSES = {
  small: {
    track: 'h-5 w-9',
    thumb: 'h-4 w-4',
    on: 'translate-x-[18px]',
  },
  medium: {
    track: 'h-6 w-11',
    thumb: 'h-5 w-5',
    on: 'translate-x-[22px]',
  },
};

const TRACK_BASE = [
  'relative inline-flex shrink-0 items-center rounded-full p-0 transition-colors',
  'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-brand-500',
  'focus-visible:ring-offset-2 focus-visible:ring-offset-surface',
  'disabled:cursor-not-allowed disabled:opacity-50',
  'motion-reduce:transition-none',
].join(' ');

const TRACK_ON = 'cursor-pointer bg-brand-600 enabled:hover:bg-brand-700';
/**
 * `content-muted`, not a `surface-*` token: the surfaces sit within a few
 * shades of the page background, so an "off" track drawn with them all but
 * disappears in light mode. The UI-component contrast floor (WCAG 1.4.11, 3:1)
 * needs the track to stand out on its own — gray-500 on the light base is
 * ~4.6:1, gray-400 on the dark base ~8:1.
 */
const TRACK_OFF = 'cursor-pointer bg-content-muted enabled:hover:bg-content-secondary';

const THUMB_BASE = [
  'pointer-events-none inline-block rounded-full bg-white shadow-sm',
  'transition-transform duration-200 motion-reduce:transition-none',
].join(' ');

function normalizeSize(size) {
  return SIZE_CLASSES[size] ? size : 'medium';
}

function joinClasses(...classes) {
  return classes.filter(Boolean).join(' ');
}

/**
 * On/off switch for settings that take effect immediately (an endpoint's
 * "Enabled", a notification preference). Use `Checkbox` instead when the value
 * is only applied on submit, or when it is one of several options in a list.
 *
 * Controlled: render `checked` from state and update it in `onChange`.
 * Accessible as `role="switch"` with `aria-checked`; Space and Enter toggle it
 * (native `<button>`). Give it a visible `label` — which also becomes clickable
 * — or, when the surrounding UI already names it, an `aria-label` /
 * `aria-labelledby`. It warns in the console when it has neither.
 *
 * @param {object} props
 * @param {boolean} [props.checked=false] — Current state.
 * @param {(next: boolean, event: import('react').MouseEvent<HTMLButtonElement>) => void} [props.onChange] — Called with the value the switch should take next.
 * @param {boolean} [props.disabled=false] — Blocks interaction and removes it from the tab order.
 * @param {'small'|'medium'} [props.size='medium'] — Track 36×20 (`small`) or 44×24 (`medium`).
 * @param {import('react').ReactNode} [props.label] — Visible label; becomes the accessible name and toggles the switch when clicked.
 * @param {import('react').ReactNode} [props.description] — Helper text under the label, announced as the switch's description. Only rendered together with `label`.
 * @param {'start'|'end'} [props.labelPosition='end'] — Label before (`start`) or after (`end`) the control.
 * @param {string} [props.id] — Id for the control. Generated when omitted.
 * @param {string} [props.className] — Classes for the outermost element.
 * @param {string} [props['aria-label']] — Accessible name when there is no visible `label`.
 * @param {string} [props['aria-labelledby']] — Id of an external element that names the switch.
 * @param {string} [props['aria-describedby']] — Extra description ids, merged with `description`.
 */
function Switch({
  checked = false,
  onChange,
  disabled = false,
  size = 'medium',
  label,
  description,
  labelPosition = 'end',
  id,
  className = '',
  'aria-label': ariaLabel,
  'aria-labelledby': ariaLabelledBy,
  'aria-describedby': ariaDescribedBy,
}) {
  const generatedId = useId();
  const switchId = id ?? `switch-${generatedId}`;
  const descriptionId = `${switchId}-description`;
  const hasLabel = label != null && label !== '';
  const hasDescription = description != null && description !== '';
  const s = normalizeSize(size);
  const sizeClasses = SIZE_CLASSES[s];

  const hasAccessibleName = hasLabel || Boolean(ariaLabel) || Boolean(ariaLabelledBy);
  const ownDescriptionId = hasDescription && hasLabel ? descriptionId : null;
  const describedBy = [ownDescriptionId, ariaDescribedBy].filter(Boolean).join(' ') || undefined;

  const handleClick = useCallback(
    (event) => {
      onChange?.(!checked, event);
    },
    [checked, onChange],
  );

  // Dev-only: left as `process.env` so the consuming app's bundler strips it.
  useEffect(() => {
    if (process.env.NODE_ENV === 'production') return;
    if (!hasAccessibleName) {
      console.warn(
        '[Switch] Missing accessible name: pass `label`, `aria-label` or `aria-labelledby`.',
      );
    }
    if (hasDescription && !hasLabel) {
      console.warn('[Switch] `description` is only rendered together with `label`.');
    }
  }, [hasAccessibleName, hasDescription, hasLabel]);

  const control = (
    <button
      id={switchId}
      type="button"
      role="switch"
      aria-checked={checked}
      aria-label={hasLabel ? undefined : ariaLabel}
      aria-labelledby={hasLabel ? undefined : ariaLabelledBy}
      aria-describedby={describedBy}
      disabled={disabled}
      data-size={s}
      onClick={handleClick}
      className={joinClasses(
        TRACK_BASE,
        sizeClasses.track,
        checked ? TRACK_ON : TRACK_OFF,
        hasLabel ? '' : className,
      )}
    >
      <span
        aria-hidden="true"
        className={joinClasses(
          THUMB_BASE,
          sizeClasses.thumb,
          checked ? sizeClasses.on : 'translate-x-0.5',
        )}
      />
    </button>
  );

  if (!hasLabel) return control;

  const text = (
    <span className="flex min-w-0 flex-col gap-0.5">
      <label
        htmlFor={switchId}
        className={joinClasses(
          'text-sm font-medium text-content',
          disabled ? 'cursor-not-allowed opacity-50' : 'cursor-pointer',
        )}
      >
        {label}
      </label>
      {hasDescription && (
        <span
          id={descriptionId}
          className={joinClasses('text-xs text-content-muted', disabled ? 'opacity-50' : '')}
        >
          {description}
        </span>
      )}
    </span>
  );

  return (
    <div className={joinClasses('inline-flex items-start gap-3', className)}>
      {labelPosition === 'start' ? text : control}
      {labelPosition === 'start' ? control : text}
    </div>
  );
}

Switch.propTypes = {
  checked: PropTypes.bool,
  onChange: PropTypes.func,
  disabled: PropTypes.bool,
  size: PropTypes.oneOf(['small', 'medium']),
  label: PropTypes.node,
  description: PropTypes.node,
  labelPosition: PropTypes.oneOf(['start', 'end']),
  id: PropTypes.string,
  className: PropTypes.string,
  'aria-label': PropTypes.string,
  'aria-labelledby': PropTypes.string,
  'aria-describedby': PropTypes.string,
};

export default memo(Switch);
